import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Copy } from 'lucide-react';
import { queryKeys } from '@/lib/api/queryKeys';
import { surveysApi } from './api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/components/ui/Toast';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuth } from '@/lib/auth/useAuth';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';

export function SurveyTemplatesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const t = useT();
  useFavoritable(t('surveys.templates.title'));
  useAuth(); // ensure auth context loaded; companyId is enforced server-side on clone

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.surveys.templates,
    queryFn: () => surveysApi.listTemplates('xotski'),
  });

  const clone = useMutation({
    mutationFn: (id: number) => surveysApi.clone(id),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.surveys.all });
      toast({ tone: 'success', title: t('surveys.toast.cloned') });
      navigate(`/surveys/${created.id}/preview`);
    },
    onError: (e) =>
      toast({ tone: 'error', title: t('surveys.toast.cloneFailed'), description: (e as Error).message }),
  });

  const templates = data?.items ?? [];

  return (
    <div className="page space-y-6">
      <header>
        <h1 className="text-3xl font-bold">{t('surveys.templates.title')}</h1>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {t('surveys.templates.subtitle')}
        </p>
      </header>

      {isLoading ? (
        <Skeleton className="h-40" />
      ) : templates.length === 0 ? (
        <EmptyState
          title={t('surveys.templates.emptyTitle')}
          description={t('surveys.templates.emptyBody')}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((tpl) => (
            <Card key={tpl.id}>
              <CardHeader>
                <Badge tone="info" className="mr-auto">
                  {t('surveys.template')}
                </Badge>
                <CardTitle>{tpl.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-sm text-[var(--color-fg-muted)]">
                  {tpl.description ||
                    t('surveys.templates.stepsCount', { n: tpl.numberOfSteps ?? 0 })}
                </p>
                <Button
                  size="sm"
                  leftIcon={<Copy className="h-4 w-4" />}
                  onClick={() => clone.mutate(tpl.id)}
                  loading={clone.isPending}
                >
                  {t('surveys.templates.useTemplate')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
